/**
 * Agent Panel overlay.
 *
 * Two-column composition: a collapsible/resizable
 * :class:`SessionSidebar` on the left lists every persisted
 * session for the active project; the right column hosts the
 * conversation, the chip strip, and the compose pill. The sidebar
 * carries the project name + path, and the active session is the
 * highlighted row inside it.
 *
 * The :class:`ProjectPicker` takes over the entire pane when no
 * project has been chosen yet, or when the user explicitly asks
 * to switch projects from the sidebar header. After picking a
 * project the overlay either resumes the most-recent session for
 * that project or starts a fresh one (see :func:`pickProject`).
 *
 * Sidebar state — open/closed and width — is persisted in
 * ``localStorage`` so the chrome reopens at the same dimensions
 * across reloads.
 */

import { Braces, FolderOpen, PanelLeftClose, PanelLeftOpen, Upload } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { fetchSessionsPage } from '@/lib/agent/api';
import { type AgentNotice, formatRateLimit, formatSessionCrash } from '@/lib/agent/errors';
import { starterPromptDisplay } from '@/lib/agent/starterPrompts';
import { codePaneDirectoryCandidates, openCodePaneFolder } from '@/lib/notebook/codePaneSync';
import { cn } from '@/lib/utils/cn';
import { ActivityChips, HealthChips, hasMetadataToShow, JumpToLatestChip } from './ActivityChips';
import { AgentImagePreviewProvider } from './AgentImagePreview';
import { AgentInput, type AgentInputApi, dataTransferHasFiles } from './AgentInput';
import { AgentLoadingState } from './AgentLoadingState';
import { useAgentPanel } from './AgentPanelProvider';
import { ProjectCwdProvider } from './blocks/ProjectCwdContext';
import { ErrorBanner } from './ErrorBanner';
import { ProjectPicker } from './ProjectPicker';
import { SessionSidebar } from './SessionSidebar';
import { TurnStream } from './TurnStream';

const SIDEBAR_OPEN_KEY = 'antioch.agentPanel.sidebarOpen';
const SIDEBAR_WIDTH_KEY = 'antioch.agentPanel.sidebarWidthPx';
const SIDEBAR_DEFAULT_WIDTH = 260;
const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 460;

function _readSidebarOpen(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const v = window.localStorage.getItem(SIDEBAR_OPEN_KEY);
    if (v === '0') return false;
    if (v === '1') return true;
  } catch {
    // localStorage may be unavailable in restricted environments.
  }
  return true;
}

function _readSidebarWidth(): number {
  if (typeof window === 'undefined') return SIDEBAR_DEFAULT_WIDTH;
  try {
    const v = window.localStorage.getItem(SIDEBAR_WIDTH_KEY);
    const parsed = v ? Number.parseInt(v, 10) : Number.NaN;
    if (!Number.isFinite(parsed)) return SIDEBAR_DEFAULT_WIDTH;
    return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, parsed));
  } catch {
    return SIDEBAR_DEFAULT_WIDTH;
  }
}

function AgentPanelOverlayImpl() {
  const panel = useAgentPanel();
  const liveBusy =
    panel.state.status !== 'idle' || panel.state.awaitingAssistant || panel.state.turnWork.startedAt !== null;
  const inputBusy = liveBusy;
  // Hard failures that take the chat surface down entirely. We
  // reserve this for things that genuinely break the session — a
  // ``server_error`` frame from the workspace server (e.g. "agent
  // session crashed") or a connection-level failure that left the
  // WS in an unrecoverable state. Rate-limit rejections do NOT
  // count as fatal: the chat is still useful, the user just has
  // to wait, so they stay on the lighter inline banner.
  const lastServerError = panel.state.errors.length > 0 ? panel.state.errors[panel.state.errors.length - 1] : null;
  const fatalNotice = formatSessionCrash(lastServerError, panel.connectionStatus);
  const isFatal = fatalNotice !== null;
  // Rate-limit banner is a non-fatal, non-modal warning — we route
  // it through ``formatRateLimit`` so the headline + reset hint
  // match the rest of the error surface, but we suppress it when a
  // fatal error has already taken the chat down so the user doesn't
  // see two stacked failures.
  const rateLimitNotice = !isFatal ? formatRateLimit(panel.state.rateLimit) : null;
  const showPicker = panel.pickerVisible || panel.cwd == null;
  const showAgentApiLoader = !panel.agentApiReady && !fatalNotice;
  const capabilities = new Set(panel.state.capabilities);
  const canUseInteractions = capabilities.has('interactions');
  const canChangePermissionMode = capabilities.has('permission_mode');
  const canChangeSpeed = capabilities.has('speed');
  // Multi-driver: every attachment can submit input. No
  // "another tab is driving" gate. The reducer's ``attachmentRole``
  // still exists for back-compat with pre-multi-driver workspace
  // servers (which won't be in production for long) but the UI no
  // longer reads it.
  const sendBlockedReason =
    panel.connectionStatus === 'connecting'
      ? 'Opening session'
      : panel.connectionStatus === 'closed'
        ? 'Reconnecting'
        : panel.connectionStatus === 'error'
          ? 'Connection failed'
          : !panel.state.attached
            ? 'Opening session'
            : null;
  const sendBlocked = sendBlockedReason !== null;

  // Hoisted scroll affordance — the chat owns the scroll DOM but
  // the "Latest" pill needs to hover over the compose surface
  // (right-aligned with the input), so we mirror the at-bottom
  // flag and grab a handle to ``jumpToBottom``. See ``TurnStream``
  // for the underlying scroll logic.
  const [chatAtBottom, setChatAtBottom] = React.useState(true);
  const jumpToBottomRef = React.useRef<(() => void) | null>(null);
  // Synchronous "stay pinned to the bottom" hook the composer can
  // call from its own textarea-grow layoutEffect. Lets the chat
  // re-pin in the same commit as the composer height change so the
  // user never sees a one-frame "chat scrolled up 22px" flicker.
  const keepChatAtBottomRef = React.useRef<(() => void) | null>(null);
  const handleComposerHeightChange = React.useCallback(() => {
    keepChatAtBottomRef.current?.();
  }, []);
  const showFloatingChips = !chatAtBottom || hasMetadataToShow(panel.state);

  // Chat-column drag-and-drop. Files dropped on the conversation
  // column route into the same upload pipeline as the textarea's
  // paperclip / paste path via the imperative handle exposed by
  // :class:`AgentInput`. We track
  // ``dragDepth`` as a counter rather than a boolean because the
  // browser fires ``dragenter`` / ``dragleave`` for every nested
  // child the cursor crosses, and a bare boolean would flicker the
  // overlay off the moment the pointer entered a child element.
  const inputApiRef = React.useRef<AgentInputApi | null>(null);
  const [dragDepth, setDragDepth] = React.useState(0);
  const uploadsDisabled = panel.cwd == null || panel.connectionStatus === 'error';
  const dragOver = dragDepth > 0 && !showPicker && !uploadsDisabled;

  // Sidebar visibility + width. Both pieces of state survive
  // reloads via ``localStorage`` so the chat chrome reopens at the
  // dimensions the user last set. Width is clamped to a reasonable
  // band so a previously-stored extreme can't render the sidebar
  // unusable on a smaller screen.
  const [sidebarOpen, setSidebarOpenState] = React.useState<boolean>(_readSidebarOpen);
  const [sidebarWidth, setSidebarWidthState] = React.useState<number>(_readSidebarWidth);
  const [sidebarResizing, setSidebarResizing] = React.useState(false);
  const setSidebarOpen = React.useCallback((next: boolean) => {
    setSidebarOpenState(next);
    try {
      window.localStorage.setItem(SIDEBAR_OPEN_KEY, next ? '1' : '0');
    } catch {
      // ignore — falls back to in-memory only.
    }
  }, []);
  const setSidebarWidth = React.useCallback((nextPx: number) => {
    const clamped = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(nextPx)));
    setSidebarWidthState(clamped);
    try {
      window.localStorage.setItem(SIDEBAR_WIDTH_KEY, String(clamped));
    } catch {
      // ignore.
    }
  }, []);

  // Picking a project from the picker: auto-resume the project's
  // most-recently-touched session so the user lands in a real chat
  // immediately. Falls back to a fresh session when there are
  // none. The sidebar covers session-switching from there.
  const pickProject = React.useCallback(
    async (nextCwd: string) => {
      try {
        const { sessions } = await fetchSessionsPage(nextCwd, { limit: 1, offset: 0 });
        const mostRecent = sessions[0];
        panel.chooseSession(nextCwd, mostRecent?.session_id ?? null);
      } catch {
        // Listing failed — start fresh; the sidebar will surface
        // the same error and a retry button once the chat mounts.
        panel.chooseSession(nextCwd, null);
      }
    },
    [panel]
  );

  const openCurrentProjectInCodePane = React.useCallback(() => {
    if (!panel.cwd) return;
    openCodePaneFolder(panel.cwd, 'agent-project', codePaneDirectoryCandidates(panel.cwd));
  }, [panel.cwd]);

  // Drag-and-drop guards. We always ``preventDefault`` on enter/over
  // when uploads are eligible — without it the browser refuses to
  // mark the pane as a valid drop target and the drop event never
  // fires. Some browsers (Safari, certain Chrome builds) don't
  // reveal ``dataTransfer.types`` on ``dragenter`` for privacy
  // reasons, so the file-check happens only on ``dragover`` /
  // ``drop`` where the payload is reliably visible.
  function handleDragEnter(e: React.DragEvent<HTMLDivElement>) {
    if (uploadsDisabled || showPicker) return;
    e.preventDefault();
    setDragDepth((d) => d + 1);
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    if (uploadsDisabled || showPicker) return;
    e.preventDefault();
    setDragDepth((d) => Math.max(0, d - 1));
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    if (uploadsDisabled || showPicker) return;
    if (!dataTransferHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    if (uploadsDisabled || showPicker) return;
    e.preventDefault();
    setDragDepth(0);
    if (!dataTransferHasFiles(e.dataTransfer)) return;
    inputApiRef.current?.ingestFiles(e.dataTransfer.files);
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden bg-white font-sans text-gray-900 dark:bg-[#0d1013] dark:text-gray-100">
      {/* Rate-limit rejections still surface as a soft inline
          banner above the chat — the chat content remains useful
          and the user just needs to wait. Fatal errors get the
          full-pane crash screen below. */}
      <ErrorBanner notice={rateLimitNotice} onDismiss={panel.clearErrors} />

      {showAgentApiLoader ? (
        <SessionLoader label="Waiting for workspace auth" />
      ) : showPicker ? (
        <ProjectPicker initialPath={panel.cwd} onOpen={pickProject} />
      ) : fatalNotice ? (
        <SessionCrash notice={fatalNotice} onRetry={panel.retrySession} onPickProject={() => panel.openPicker()} />
      ) : (
        <ProjectCwdProvider cwd={panel.cwd}>
          {/* ``min-w-0`` on the row + chat column is critical: without
              it, flex items default to ``min-width: auto`` (= their
              children's min-content), and a long bash-command tool row
              or a fixed-width compose surface bubbles intrinsic width
              up the tree. The pane then *visibly* refuses to shrink
              past whatever the widest tool block wants — text-only
              chats fold up cleanly, but anything with a ``BASH`` /
              ``MONITOR`` / ``EDIT`` row was pinned. With ``min-w-0``
              the chat column shrinks freely and the row's own
              ``truncate`` finally has room to do its job. */}
          <div className="flex min-h-0 min-w-0 flex-1">
            {/* Sidebar stays mounted across session switches so its
                project header + session list don't blink off while a
                new chat stands up. The chat column carries its own
                loader for that case (see ``SessionLoader`` below) —
                "loading the full pane" only happens before any
                project has been chosen, which is handled by
                :class:`ProjectPicker` above. */}
            {panel.cwd && (
              <div
                aria-hidden={!sidebarOpen}
                inert={sidebarOpen ? undefined : true}
                className={cn(
                  'min-h-0 flex-none overflow-hidden',
                  sidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
                  !sidebarResizing && 'transition-[width,opacity] duration-200 ease-out'
                )}
                style={{ width: sidebarOpen ? `${sidebarWidth}px` : '0px' }}
              >
                <SidebarColumn
                  cwd={panel.cwd}
                  width={sidebarWidth}
                  // Optimistic highlight: use the *intended* session id
                  // (``resumeSessionId``) so a click lights up the row
                  // synchronously, before the WS handshake resolves.
                  // Falls back to the actually-attached id so brand-new
                  // sessions (where resumeSessionId is null until a new
                  // server-assigned id lands) still highlight once
                  // ``session_attached`` arrives.
                  currentSessionId={panel.resumeSessionId ?? panel.state.sessionId}
                  onChooseSession={(sessionId) => panel.chooseSession(panel.cwd as string, sessionId)}
                  onResize={setSidebarWidth}
                  onResizeActiveChange={setSidebarResizing}
                />
              </div>
            )}
            <div
              className="relative flex min-h-0 min-w-0 flex-1 flex-col"
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <AgentImagePreviewProvider>
                {panel.sessionLoading ? (
                  <SessionLoader label="Loading session" />
                ) : (
                  <>
                    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
                      <ChatPaneToolbar
                        cwd={panel.cwd}
                        sidebarOpen={sidebarOpen}
                        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                        onOpenAgentProject={panel.openPicker}
                        onOpenCodeProject={openCurrentProjectInCodePane}
                      />
                      <TurnStream
                        state={panel.state}
                        hasMoreHistory={panel.historyHasMore}
                        loadingHistory={panel.historyLoading}
                        onLoadOlder={panel.loadOlderHistory}
                        onAtBottomChange={setChatAtBottom}
                        onPickStarter={(starter) => {
                          if (panel.sendInput(starter.prompt, starterPromptDisplay(starter))) return;
                          inputApiRef.current?.setText(starter.prompt);
                        }}
                        onRespondToInteraction={panel.respondToInteraction}
                        canRespondToInteractions={canUseInteractions}
                        jumpToBottomRef={jumpToBottomRef}
                        keepAtBottomRef={keepChatAtBottomRef}
                        onStopTask={panel.stopTask}
                      />
                      {/* Bottom fade — a thin gradient that sits flush
                        with the top edge of the metadata / compose
                        surface so chat content appears to dissolve
                        into the input area as it scrolls past. */}
                      <div
                        aria-hidden
                        className={cn(
                          'pointer-events-none absolute right-4 bottom-0 left-0 z-10 h-6',
                          'bg-gradient-to-b from-white/0 to-white',
                          'dark:from-[#0d1013]/0 dark:to-[#0d1013]'
                        )}
                      />
                      {/* Floating chip rail: Latest + activity + health
                        chips share one right-aligned row above the
                        compose surface. Keeping the whole rail out of
                        normal layout prevents the chat viewport height
                        from changing as chips appear/disappear, which
                        avoids scrollTop auto-clamps at the bottom
                        edge. The row intentionally never wraps: on
                        narrow panes we clip lower-priority left-side
                        chips instead of stacking metadata vertically
                        over the composer. */}
                      <div
                        aria-hidden={!showFloatingChips}
                        inert={showFloatingChips ? undefined : true}
                        className={cn(
                          'pointer-events-none absolute bottom-0 left-0 z-20 flex w-full justify-center px-7 transition-[opacity,transform] duration-150 ease-out',
                          showFloatingChips ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0'
                        )}
                      >
                        <div className="flex w-full min-w-0 max-w-[860px] items-center justify-end gap-1.5 overflow-hidden whitespace-nowrap">
                          <ActivityChips
                            state={panel.state}
                            onStopTask={panel.stopTask}
                            className="pointer-events-auto"
                          />
                          <HealthChips state={panel.state} className="pointer-events-auto" />
                          {!chatAtBottom && (
                            <JumpToLatestChip
                              onClick={() => jumpToBottomRef.current?.()}
                              className="fade-in-0 zoom-in-95 pointer-events-auto animate-in duration-150 ease-out"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex min-w-0 flex-none flex-col">
                      <AgentInput
                        busy={inputBusy}
                        onSend={panel.sendInput}
                        onInterrupt={panel.interrupt}
                        disabled={panel.connectionStatus === 'error'}
                        sendBlocked={sendBlocked}
                        sendBlockedReason={sendBlockedReason}
                        cwd={panel.cwd}
                        connectionStatus={panel.connectionStatus}
                        attached={panel.state.attached}
                        pendingUserInputIds={panel.pendingUserInputIds}
                        lastAcceptedClientMessageId={panel.lastAcceptedClientMessageId}
                        effort={panel.state.effort}
                        onChangeEffort={panel.setEffort}
                        speed={panel.state.speed}
                        onChangeSpeed={canChangeSpeed ? panel.setSpeed : undefined}
                        permissionMode={panel.state.permissionMode}
                        onChangePermissionMode={panel.setPermissionMode}
                        canChangePermissionMode={canChangePermissionMode}
                        slashCommands={panel.state.slashCommands}
                        apiRef={inputApiRef}
                        draftKeyWorkspaceId={panel.workspaceId}
                        draftKeyCwd={panel.cwd}
                        draftKeySessionId={panel.state.sessionId}
                        onComposerHeightChange={handleComposerHeightChange}
                      />
                    </div>
                  </>
                )}
              </AgentImagePreviewProvider>
              {dragOver && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-2 z-50 flex items-center justify-center rounded-lg border border-gray-300 border-dashed bg-gray-50/85 text-gray-700 shadow-sm backdrop-blur-sm dark:border-white/15 dark:bg-gray-950/70 dark:text-gray-200"
                >
                  <div className="flex items-center gap-2 rounded-md bg-white/70 px-3 py-2 text-[13px] shadow-sm dark:bg-gray-950/60">
                    <Upload className="size-4" />
                    <span>Drop files to attach to your next message</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ProjectCwdProvider>
      )}
    </div>
  );
}

interface SidebarColumnProps {
  cwd: string;
  width: number;
  currentSessionId: string | null;
  onChooseSession: (sessionId: string | null) => void;
  onResize: (px: number) => void;
  onResizeActiveChange: (active: boolean) => void;
}

/**
 * Sidebar host with a built-in vertical drag handle on its right
 * edge. The handle uses pointer events directly (rather than a
 * library) because the tracked dimension is just one number and
 * the lift in dependency weight isn't worth it. We anchor the
 * sidebar at ``flex-none`` so its width doesn't get squeezed by
 * the chat column's ``flex-1`` grow.
 */
function SidebarColumn({
  cwd,
  width,
  currentSessionId,
  onChooseSession,
  onResize,
  onResizeActiveChange,
}: SidebarColumnProps) {
  const [dragging, setDragging] = React.useState(false);
  const startRef = React.useRef<{ x: number; w: number } | null>(null);

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    startRef.current = { x: e.clientX, w: width };
    setDragging(true);
    onResizeActiveChange(true);
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragging || !startRef.current) return;
    const delta = e.clientX - startRef.current.x;
    onResize(startRef.current.w + delta);
  }

  function endDrag(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragging) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
    onResizeActiveChange(false);
    startRef.current = null;
  }

  return (
    <div
      className="relative h-full flex-none border-gray-100/80 border-r dark:border-white/[0.06]"
      style={{ width: `${width}px` }}
    >
      <SessionSidebar
        cwd={cwd}
        currentSessionId={currentSessionId}
        onChooseSession={onChooseSession}
        className="h-full border-r-0"
      />
      {/* Hairline drag handle on the sidebar's right edge. The
          column owns the persistent full-height divider; this
          pointer target only strengthens that same line on hover/drag.
          The underlying hit target is a 6px strip overlapping the
          divider so the grab still feels comfortable.
          Pointer-only affordance — keyboard users get the same
          collapse via the sidebar header's collapse button, so we
          mark this hidden from the a11y tree rather than expose a
          ``separator`` role with no keyboard control. */}
      <div
        aria-hidden
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="group absolute top-0 right-[-3px] bottom-0 z-20 w-[6px] cursor-col-resize"
      >
        <div
          className={cn(
            'absolute inset-y-0 left-[2px] w-px transition-colors',
            dragging
              ? 'bg-gray-400 dark:bg-gray-500'
              : 'bg-transparent group-hover:bg-gray-300 dark:group-hover:bg-white/20'
          )}
        />
      </div>
    </div>
  );
}

/**
 * Centered "session is standing up" placeholder. Shown for the
 * brief window between the user picking a session and the panel
 * having all the data needed to render a stable header (session
 * name, project metadata) and chat surface (history tail). Keeps
 * the user from seeing a half-built header pop in followed by an
 * empty turn stream filling out a moment later.
 */
function SessionLoader({ label }: { label: string }) {
  const title = label === 'Waiting for workspace auth' ? 'Waiting for workspace auth' : 'Opening session';
  const description = label === 'Waiting for workspace auth' ? 'Preparing workspace credentials' : undefined;
  return <AgentLoadingState title={title} description={description} />;
}

interface ChatPaneToolbarProps {
  cwd: string | null;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onOpenAgentProject: () => void;
  onOpenCodeProject: () => void;
}

function ChatPaneToolbar({
  cwd,
  sidebarOpen,
  onToggleSidebar,
  onOpenAgentProject,
  onOpenCodeProject,
}: ChatPaneToolbarProps) {
  return (
    <div
      className={cn(
        // A compact glass control surface: translucent enough to pick up the
        // chat underneath, but still has a bright rim so icons stay legible.
        'absolute top-4 left-4 isolate z-30 flex items-center gap-0.5 overflow-hidden rounded-full border p-1',
        'border-white/60 bg-white/35 shadow-[0_16px_30px_-18px_rgba(15,23,42,0.5),inset_0_1px_0_rgba(255,255,255,0.72),inset_0_-1px_0_rgba(15,23,42,0.045)] backdrop-blur-2xl backdrop-saturate-150',
        'before:pointer-events-none before:absolute before:inset-x-3 before:top-0 before:z-0 before:h-px before:bg-white/75',
        'after:pointer-events-none after:absolute after:inset-0 after:z-0 after:rounded-full after:bg-[radial-gradient(circle_at_26%_10%,rgba(255,255,255,0.6),transparent_34%),linear-gradient(135deg,rgba(255,255,255,0.22),rgba(255,255,255,0.055)_46%,rgba(15,23,42,0.035))] after:opacity-65 after:mix-blend-screen',
        'dark:border-white/12 dark:bg-[#111820]/32 dark:shadow-[0_18px_34px_-20px_rgba(0,0,0,0.78),inset_0_1px_0_rgba(255,255,255,0.1),inset_0_-1px_0_rgba(0,0,0,0.36)]',
        'dark:after:bg-[radial-gradient(circle_at_24%_10%,rgba(255,255,255,0.18),transparent_34%),linear-gradient(135deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02)_46%,rgba(0,0,0,0.16))] dark:after:opacity-85 dark:after:mix-blend-normal dark:before:bg-white/18'
      )}
    >
      <ToolbarIconButton
        label={sidebarOpen ? 'Close sessions sidebar' : 'Open sessions sidebar'}
        tooltip="Open/close sessions sidebar"
        onClick={onToggleSidebar}
      >
        {sidebarOpen ? (
          <PanelLeftClose className="size-3.5" strokeWidth={1.75} />
        ) : (
          <PanelLeftOpen className="size-3.5" strokeWidth={1.75} />
        )}
      </ToolbarIconButton>
      <ToolbarIconButton
        label="Show project files in Agent Pane"
        tooltip="Go to project files"
        disabled={!cwd}
        onClick={onOpenAgentProject}
      >
        <FolderOpen className="size-3.5" strokeWidth={1.85} />
      </ToolbarIconButton>
      <ToolbarIconButton
        label="Open current project files in Code Pane"
        tooltip="Open project folder in code pane"
        disabled={!cwd}
        onClick={onOpenCodeProject}
      >
        <Braces className="size-3.5" strokeWidth={2} />
      </ToolbarIconButton>
    </div>
  );
}

interface ToolbarIconButtonProps {
  label: string;
  tooltip: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function ToolbarIconButton({ label, tooltip, disabled, onClick, children }: ToolbarIconButtonProps) {
  return (
    <SimpleTooltip content={tooltip} side="bottom">
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        className={cn(
          'relative z-10 flex size-7 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full text-slate-600 transition-[background-color,color,box-shadow] duration-200 ease-out',
          'hover:bg-white/45 hover:text-slate-950 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_6px_14px_-12px_rgba(15,23,42,0.48)]',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500/55 focus-visible:outline-offset-2',
          'disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-600 disabled:hover:shadow-none',
          'dark:text-slate-300 dark:hover:bg-white/[0.12] dark:hover:text-white dark:hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_8px_16px_-14px_rgba(0,0,0,0.9)] dark:disabled:hover:bg-transparent dark:disabled:hover:text-slate-300'
        )}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        {children}
      </button>
    </SimpleTooltip>
  );
}

interface SessionCrashProps {
  notice: AgentNotice;
  onRetry: () => void;
  onPickProject: () => void;
}

/**
 * Full-pane failure screen. Replaces the chat surface when the
 * workspace server hands us a ``server_error`` frame (e.g. "agent
 * session crashed") or the WebSocket ends up in an unrecoverable
 * connection state. Mirrors the loader's calm centered layout so
 * a flaky session doesn't look like a different product mode —
 * just a different terminal state for the same panel.
 *
 * Headline / icon / hint are all driven by
 * :func:`formatSessionCrash` so an auth failure ("run ``antioch
 * login``"), a connection drop ("server may be restarting"), and
 * a process crash all render with their own designed copy
 * instead of a single generic "something went wrong" string. The
 * raw underlying error is preserved on hover via the title
 * attribute so power users can still see it.
 *
 * Two recovery affordances:
 *
 *  - **Retry** — the primary action. Tears the dead WS down,
 *    clears the sticky error, and forces the auto-connect effect
 *    to dial again. Reducer state (turns, history) is preserved
 *    so a successful reconnect drops the user right back into the
 *    same scrollback.
 *  - **Switch project or session** — escape hatch when retry
 *    keeps failing. Hands the user back to the project picker
 *    instead of trapping them on a broken session.
 */
function SessionCrash({ notice, onRetry, onPickProject }: SessionCrashProps) {
  return (
    <ErrorState
      icon={notice.icon}
      title={notice.headline}
      description={
        notice.hint ? (
          <span title={notice.raw} className="break-words">
            {notice.hint}
          </span>
        ) : undefined
      }
      onRetry={onRetry}
    >
      <Button type="button" variant="outline" size="sm" onClick={onPickProject}>
        Switch project or session
      </Button>
    </ErrorState>
  );
}

// Rendered with no props (`<AgentPanelOverlay />`), so memoizing it stops the
// whole agent surface (transcript, editor, session sidebar) from reconciling
// on every parent re-render — e.g. each snap-drag mousemove frame or sim-status
// tick in StreamOnlyWindow. React.memo does NOT block its own context/state
// updates, so live agent activity still renders normally.
export const AgentPanelOverlay = React.memo(AgentPanelOverlayImpl);
